import { makeAutoObservable } from "mobx";
import type { Map as MapboxMap, MapMouseEvent } from "mapbox-gl";
import type { ReactNode } from "react";

export const RIGHT_CLICK_MENU_DATA_ATTR = "data-right-click-menu";

export interface RightClickLocation {
	lng: number;
	lat: number;
}

/**
 * Turns a raw right-click on the map into a domain-specific target. The menu
 * store stays agnostic about what was clicked — callers inject this resolver to
 * decide (e.g. marker vs. connection vs. empty map).
 */
export type RightClickTargetResolver<TTarget> = (params: {
	map: MapboxMap;
	point: { x: number; y: number };
	location: RightClickLocation;
}) => TTarget;

export interface RightClick<TTarget> extends RightClickLocation {
	target: TTarget;
}

export interface RightClickMenuPosition<TTarget> extends RightClick<TTarget> {
	x: number;
	y: number;
}

export interface RightClickMenuAction<TTarget> {
	id: string;
	label: string;
	icon?: ReactNode;
	onSelect: (click: RightClick<TTarget>) => void;
}

export class RightClickMenuStore<TTarget> {
	position: RightClickMenuPosition<TTarget> | null = null;

	private map: MapboxMap | null = null;

	constructor(private resolveTarget: RightClickTargetResolver<TTarget>) {
		makeAutoObservable(this, { map: false, resolveTarget: false } as any);
	}

	attach = (map: MapboxMap) => {
		this.map = map;
		map.on("contextmenu", this.handleMapContextMenu);
		map.on("move", this.close);
	};

	detach = () => {
		if (this.map) {
			this.map.off("contextmenu", this.handleMapContextMenu);
			this.map.off("move", this.close);
		}
		this.map = null;
		this.stopDocumentListeners();
	};

	open = (x: number, y: number, location: RightClickLocation, target: TTarget) => {
		this.position = { x, y, lng: location.lng, lat: location.lat, target };
		requestAnimationFrame(() => {
			if (!this.position) return;
			this.startDocumentListeners();
		});
	};

	close = () => {
		this.position = null;
		this.stopDocumentListeners();
	};

	private handleMapContextMenu = (event: MapMouseEvent) => {
		event.originalEvent.preventDefault();
		if (!this.map) return;
		const location: RightClickLocation = { lng: event.lngLat.lng, lat: event.lngLat.lat };
		const target = this.resolveTarget({ map: this.map, point: event.point, location });
		this.open(event.point.x, event.point.y, location, target);
	};

	private handleDocumentClick = (event: MouseEvent) => {
		const target = event.target;
		if (target instanceof Element && target.closest(`[${RIGHT_CLICK_MENU_DATA_ATTR}]`)) return;
		this.close();
	};

	private handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === "Escape") this.close();
	};

	private startDocumentListeners = () => {
		document.addEventListener("mousedown", this.handleDocumentClick);
		document.addEventListener("contextmenu", this.handleDocumentClick);
		document.addEventListener("keydown", this.handleKeyDown);
	};

	private stopDocumentListeners = () => {
		document.removeEventListener("mousedown", this.handleDocumentClick);
		document.removeEventListener("contextmenu", this.handleDocumentClick);
		document.removeEventListener("keydown", this.handleKeyDown);
	};
}
